import React from "react";
import { ChevronRight, Layers } from "lucide-react";

const LEAD_STATUSES = [
    { label: "New Lead", value: "NEW", color: "bg-blue-500" },
    { label: "Contacted", value: "CONTACTED", color: "bg-sky-400" },
    { label: "Qualified", value: "QUALIFIED", color: "bg-emerald-500" },
    { label: "Proposal", value: "PROPOSAL", color: "bg-amber-400" },
    { label: "Negotiation", value: "NEGOTIATION", color: "bg-cyan-500" },
    { label: "Won", value: "WON", color: "bg-emerald-600" },
    { label: "Lost", value: "LOST", color: "bg-red-500" },
];

const PRIORITIES = [
    { label: "None", value: "NONE", color: "bg-slate-300" },
    { label: "Low", value: "LOW", color: "bg-slate-400" },
    { label: "Medium", value: "MEDIUM", color: "bg-blue-500" },
    { label: "High", value: "HIGH", color: "bg-amber-500" },
    { label: "Critical", value: "CRITICAL", color: "bg-red-500" },
];

const DUMMY_TAGS = [
    { id: 1, name: "New Customer", color: "bg-blue-500" },
    { id: 2, name: "Startup", color: "bg-cyan-500" },
    { id: 3, name: "Agency", color: "bg-emerald-500" },
    { id: 4, name: "Existing Customer", color: "bg-amber-500" },
];

const ACTIVITY_OPTIONS = [
    { label: "Call", value: "CALL", color: "bg-blue-500" },
    { label: "Follow-up", value: "FOLLOWUP", color: "bg-amber-500" },
    { label: "Note", value: "NOTE", color: "bg-slate-400" },
    { label: "Meeting", value: "MEETING", color: "bg-cyan-500" },
    { label: "Email", value: "EMAIL", color: "bg-emerald-500" },
    { label: "WhatsApp", value: "WHATSAPP", color: "bg-green-500" },
];

export default function BulkActionOptionsDropdown({ action, developers, stages, onSelect }) {
    const getOptions = () => {
        switch (action) {
            case "ASSIGN_USER":
                return developers.map((user) => ({
                    label: user.name || user.full_name || user.email,
                    value: user.id,
                    color: "bg-cyan-500",
                }));

            case "CHANGE_STAGE":
                return stages.length
                    ? stages.map((s) => ({ label: s.name, value: s.id, color: "bg-blue-500" }))
                    : LEAD_STATUSES;

            case "CHANGE_STATUS":
                return LEAD_STATUSES;

            case "CHANGE_PRIORITY":
                return PRIORITIES;

            case "ADD_TAG":
            case "REPLACE_TAG":
                return DUMMY_TAGS.map((tag) => ({
                    label: tag.name,
                    value: tag.id,
                    color: tag.color,
                }));

            case "ACTIVITY":
                return ACTIVITY_OPTIONS;

            default:
                return [];
        }
    };

    const options = getOptions();

    return (
    <div className="w-[248px] overflow-hidden rounded-2xl border border-[#d6e5eb] bg-white shadow-[0_16px_40px_rgba(15,43,66,0.14)]">
        {/* Header */}
        <div className="flex h-12 items-center justify-between border-b border-[#e5eef2] px-3.5">
            <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-[#50b8e7]" />

                <span className="text-[13px] font-semibold text-[#647d8e]">
                    Select option
                </span>
            </div>

            <span className="text-xs font-medium text-[#9aadb7]">
                {options.length}
            </span>
        </div>

        {/* Options */}
        <div className="max-h-[320px] overflow-y-auto p-1.5">
            {options.map((option) => (
                <button
                    key={option.value}
                    type="button"
                    onClick={() => onSelect(option.value)}
                    className="group flex min-h-10 w-full items-center rounded-xl px-2.5 text-left transition-colors duration-100 hover:bg-[#f5fafc] focus:outline-none focus-visible:bg-[#edf8fb]"
                >
                    
                    {/* Label */}
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-[#526e7e] group-hover:text-[#173044]" title={option.label}>
                        {option.label}
                    </span>

                    {/* Arrow */}
                    <ChevronRight
                        size={14}
                        strokeWidth={1.8}
                        className="ml-2 shrink-0 text-[#afc0c9] opacity-0 transition-all duration-100 group-hover:translate-x-0.5 group-hover:text-[#647d8e] group-hover:opacity-100"
                    />
                </button>
            ))}

            {/* Empty */}
            {options.length === 0 && (
                <div className="flex flex-col items-center justify-center px-4 py-10">
                    <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-lg bg-[#edf5f8] text-[#8197a4]">
                        <Layers size={15} strokeWidth={1.8} />
                    </div>

                    <span className="text-[13px] font-medium text-[#647d8e]">
                        No options available
                    </span>

                    <span className="mt-0.5 text-xs text-[#8197a4]">
                        Nothing to select
                    </span>
                </div>
            )}
        </div>
    </div>
);
}
